import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {merge, Observable, ReplaySubject} from 'rxjs';
import {Partner} from '../../../shared/interface/partner';
import {LogService} from '../../../shared/services/log.service';
import {delay, mergeMap, takeUntil} from 'rxjs/operators';
import {MatPaginator} from '@angular/material/paginator';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {Log} from '../../../shared/interface/log';
import {PartnerFacility} from '../../../shared/interface/partner-facility';
import {Ofwca} from '../../../shared/interface/ofwca';
import {Document} from '../../../shared/interface/document';
import {Contract} from '../../../shared/interface/contract';
import {User} from '../../../shared/interface/user';
import {Krk} from '../../../shared/interface/krk';
import {Registration} from '../../../shared/interface/registration';
import {DeregistrationPack} from '../../../shared/interface/deregistration-pack';
import {RegistrationIdentifier} from '../../../shared/interface/registration-identifier';
import {Meeting} from '../../../shared/interface/meeting';
import {CommissionRedirection} from '../../../shared/interface/commission-redirection';
import {CommissionImportRow} from '../../../shared/interface/commission-import-row';
import {Invoice} from '../../../shared/interface/invoice';
import {Newsletter} from "../../../shared/interface/newsletter";
import {Regional} from "../../../shared/interface/regional";
import {InsuranceCompany} from "../../../shared/interface/insurance-company";
import {Support} from "../../../shared/interface/support";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";
import {SERVICE_TYPE_CORPORATION} from "../../../shared/services/dict.service";
import {Post} from "../../../shared/interface/post";
import {ReceivablesPaymentImportRow} from "../../../shared/interface/receivables-payment-import-row";
import {ReceivablesImportRow} from "../../../shared/interface/receivables-import-row";
import {IddTrainingTrainer} from "../../../shared/interface/idd-training-trainer";
import {Template} from "../../../shared/interface/template";
import {CommissionUpdatePackage} from "../../../shared/interface/commission-update-package";
import {ReceivablesBalance} from "../../../shared/interface/receivables-balance";
import {LicensedAgent} from "../../../shared/interface/licensed-agent";
import {Terminals} from "../../../shared/interface/terminals";
import {TerminalsImportRow} from "../../../shared/interface/terminals-import-row";
import {AffiliateCode} from "../../../shared/interface/affiliateCode";
import {PersonalDataChange} from "../../../shared/interface/personalDataChange";
import {ReceivablesTransfer} from "../../../shared/interface/receivables-transfer";
import {Employee, LeaveApplication} from "../../../shared/interface/employee";
import {License} from "../../../shared/interface/license";
import {Device} from "../../../shared/interface/device";
import {WarehouseProduct} from "../../../shared/interface/warehouse-product";
import {AdditionalField} from "../../../shared/interface/additionalField";
import {CompetitionImportRow} from "../../../shared/interface/competition-import-row";
import {HiperioRegistrationConfig} from "../../../shared/interface/hiperio-registration-config";
import {HiperioConfigDirect} from "../../../shared/interface/hiperio-config-direct";
import {InvoiceDocument} from "../../../shared/interface/invoice-document";
import {CommissionTable} from "../../../shared/interface/commission-table";
import {RiskConfiguration} from "../../../shared/interface/risk-configuration";

@Component({
  selector: 'app-log-list',
  templateUrl: './log-list.component.html',
  styleUrls: ['./log-list.component.scss']
})
export class LogListComponent implements OnDestroy, AfterViewInit {
  @Input() partner?: Observable<Partner>;
  @Input() facility?: Observable<PartnerFacility>;
  @Input() ofwca?: Observable<Ofwca>;
  @Input() document?: Observable<Document>;
  @Input() newsletter?: Observable<Newsletter>;
  @Input() contract?: Observable<Contract>;
  @Input() krk?: Observable<Krk>;
  @Input() registration?: Observable<Registration>;
  @Input() deregistrationPack?: Observable<DeregistrationPack>;
  @Input() identifier?: Observable<RegistrationIdentifier>;
  @Input() meeting?: Observable<Meeting>;
  @Input() commissionRedirection?: Observable<CommissionRedirection>;
  @Input() commissionImportRow?: Observable<CommissionImportRow>;
  @Input() commissionUpdatePackage?: Observable<CommissionUpdatePackage>;
  @Input() regional?: Observable<Regional>;
  @Input() invoice?: Observable<Invoice>;
  @Input() invoiceDocument?: Observable<InvoiceDocument>;
  @Input() insuranceCompany?: Observable<InsuranceCompany>;
  @Input() support?: Observable<Support>;
  @Input() post?: Observable<Post>;
  @Input() user?: Observable<User>;
  @Input() iddTrainingTrainer?: Observable<IddTrainingTrainer>;
  @Input() receivablesPaymentImportRow?: Observable<ReceivablesPaymentImportRow>;
  @Input() receivablesImportsRow?: Observable<ReceivablesImportRow>;
  @Input() template?: Observable<Template>;
  @Input() receivablesBalance?: Observable<ReceivablesBalance>;
  @Input() licensedAgent?: Observable<LicensedAgent>;
  @Input() terminals?: Observable<Terminals>;
  @Input() terminalsImportRow?: Observable<TerminalsImportRow>;
  @Input() affiliateCode?: Observable<AffiliateCode>;
  @Input() personalDataChange?: Observable<PersonalDataChange>;
  @Input() receivablesTransfer?: Observable<ReceivablesTransfer>;
  @Input() leaveApplication?: Observable<LeaveApplication>;
  @Input() employee?: Observable<Employee>;
  @Input() license?: Observable<License>;
  @Input() device?: Observable<Device>;
  @Input() warehouseProduct?: Observable<WarehouseProduct>;
  @Input() additionalField?: Observable<AdditionalField>;
  @Input() competitionImportRow?: Observable<CompetitionImportRow>;
  @Input() hiperioRegistrationConfig?: Observable<HiperioRegistrationConfig>;
  @Input() hiperioConfigDirect?: Observable<HiperioConfigDirect>;
  @Input() commissionTable?: Observable<CommissionTable>;
  @Input() riskConfiguration?: Observable<RiskConfiguration>;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  displayedColumns: string[] = ['user', 'createdAt', 'note', 'field', 'previous', 'new', ];
  logs: Log[];
  total: number;
  createdAt?: string;
  createdBy?: User;
  userPermissions: ReplaySubject<UserPermissions>;


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private logService: LogService,
    private auth: AuthService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.logService.getLogListSubject()
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((result: CountedAndPaginated<Log>) => {
        this.logs = result.items;
        this.total = result.pagination.total;
    });
  }

  ngAfterViewInit(): void {
    if (this.partner) {
      merge(this.partner, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.partner)
        )
        .subscribe((partner: Partner) => {
          this.logService.loadPartnerLogList(partner.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = partner.createdAt;
          this.createdBy = partner.createdBy;
        });
    }

    if (this.facility) {
      merge(this.facility, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.facility)
        )
        .subscribe((partnerFacility: PartnerFacility) => {
          this.logService.loadPartnerFacilityLogList(partnerFacility.address.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = partnerFacility.createdAt;
          this.createdBy = partnerFacility.createdBy;
        });
    }

    if (this.ofwca) {
      merge(this.ofwca, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.ofwca)
        )
        .subscribe((ofwca: Ofwca) => {
          this.logService.loadOfwcaLogList(ofwca.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = ofwca.createdAt;
          this.createdBy = ofwca.createdBy;
        });
    }

    if (this.document) {
      merge(this.document, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.document)
        )
        .subscribe((document: Document) => {
          this.logService.loadDocumentLogList(document.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = document.createdAt;
          this.createdBy = document.user;
        });
    }
    if (this.newsletter) {
      merge(this.newsletter, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.newsletter)
        )
        .subscribe((newsletter: Newsletter) => {
          this.logService.loadNewsletterLogList(newsletter.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = newsletter.createdAt;
          this.createdBy = newsletter.user;
        });
    }

    if (this.contract) {
      merge(this.contract, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.contract)
        )
        .subscribe((contract: Contract) => {
          this.logService.loadContractLogList(contract.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = contract.createdAt;
          this.createdBy = contract.createdBy;
        });
    }

    if (this.krk) {
      merge(this.krk, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.krk)
        )
        .subscribe((krk: Krk) => {
          this.logService.loadKrkLogList(krk.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = krk.createdAt;
          this.createdBy = krk.createdBy;
        });
    }

    if (this.registration) {
      merge(this.registration, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.registration)
        )
        .subscribe((registration: Registration) => {
          this.logService.loadRegistrationLogList(registration.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = registration.createdAt;
          this.createdBy = registration.createdBy;
        });
    }

    if (this.deregistrationPack) {
      merge(this.deregistrationPack, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.deregistrationPack)
        )
        .subscribe((deregistrationPack: DeregistrationPack) => {
          this.logService.loadDeregistrationPackLogList(deregistrationPack.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = deregistrationPack.createdAt;
          this.createdBy = deregistrationPack.createdBy;
        });
    }

    if (this.identifier) {
      merge(this.identifier, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.identifier)
        )
        .subscribe((identifier: RegistrationIdentifier) => {
          this.logService.loadIdentifierLogList(identifier.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = identifier.createdAt;
          this.createdBy = identifier.createdBy;
        });
    }

    if (this.meeting) {
      merge(this.meeting, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.meeting)
        )
        .subscribe((meeting: Meeting) => {
          this.logService.loadMeetingLogList(meeting.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = meeting.createdAt;
          this.createdBy = meeting.createdBy;
        });
    }

    if (this.commissionRedirection) {
      merge(this.commissionRedirection, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.commissionRedirection)
        )
        .subscribe((commissionRedirection: CommissionRedirection) => {
          this.logService.loadCommissionRedirectionLogList(commissionRedirection.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = commissionRedirection.createdAt;
          this.createdBy = commissionRedirection.createdBy;
        });
    }

    if (this.commissionImportRow) {
      merge(this.commissionImportRow, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.commissionImportRow)
        )
        .subscribe((commissionImportRow: CommissionImportRow) => {
          this.logService.loadCommissionImportRowLogList(commissionImportRow.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = commissionImportRow.createdAt;
          this.createdBy = null;
        });
    }
  if (this.commissionUpdatePackage) {
      merge(this.commissionUpdatePackage, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.commissionUpdatePackage)
        )
        .subscribe((commissionUpdatePackage: CommissionUpdatePackage) => {
          this.logService.loadCommissionUpdatePackageLogList(commissionUpdatePackage.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = commissionUpdatePackage.createdAt;
          this.createdBy = null;
        });
    }

    if (this.regional) {
      merge(this.regional, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.regional)
        )
        .subscribe((regional: Regional) => {
          this.logService.loadRegionalRowLogList(regional.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = null;
          this.createdBy = regional.createdBy;
        });
    }

    if (this.invoice) {
      merge(this.invoice, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.invoice)
        )
        .subscribe((invoice: Invoice) => {
          this.logService.loadInvoiceLogList(invoice.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = invoice.createdAt;
          this.createdBy = invoice.createdBy;
        });
    }
    if (this.insuranceCompany) {
      merge(this.insuranceCompany, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.insuranceCompany)
        )
        .subscribe((insuranceCompany: InsuranceCompany) => {
          this.logService.loadInsuranceCompanyLogList(insuranceCompany.id, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = null;
          this.createdBy = null;
        });
    }
    if (this.support) {
      merge(this.support, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.support)
        )
        .subscribe((support: Support) => {
          if (support.serviceType.key === SERVICE_TYPE_CORPORATION) {
            this.logService.loadCorporationLogList(support.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          }else{
            this.logService.loadSupportLogList(support.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          }
          this.createdAt = support.createdAt;
          this.createdBy = support.createdBy;
        });
    }
    if (this.post) {
      merge(this.post, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.post)
        )
        .subscribe((post: Post) => {
          this.logService.loadPost360LogList(post.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = post.createdAt;
          this.createdBy = post.createdBy;
        });
    }
    if (this.user) {
      merge(this.user, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.user)
        )
        .subscribe((user: User) => {
          this.logService.loadUserLogList(user.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = user.createdAt;
          this.createdBy = user.createdBy;
        });
    }
    if (this.receivablesPaymentImportRow) {
      merge(this.receivablesPaymentImportRow, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.receivablesPaymentImportRow)
        )
        .subscribe((receivablesPaymentImportRow: ReceivablesPaymentImportRow) => {
          this.logService.loagReceivablesPaymentImportRowLogList(receivablesPaymentImportRow.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = receivablesPaymentImportRow.createdAt;
          this.createdBy = receivablesPaymentImportRow?.createdBy;
        });
    }
    if (this.receivablesImportsRow) {
      merge(this.receivablesImportsRow, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.receivablesImportsRow)
        )
        .subscribe((receivablesImportRow: ReceivablesImportRow) => {
          this.logService.loagReceivablesImportRowLogList(receivablesImportRow.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = receivablesImportRow.createdAt;
          this.createdBy = receivablesImportRow?.import?.createdBy;
        });
    }
    if (this.iddTrainingTrainer) {
      merge(this.iddTrainingTrainer, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.iddTrainingTrainer)
        )
        .subscribe((iddTrainingTrainer: IddTrainingTrainer) => {
          this.logService.loadIddTrainingTrainerLogList(iddTrainingTrainer.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = iddTrainingTrainer.createdAt;
          this.createdBy = iddTrainingTrainer.createdBy;
        });
    }
    if (this.template) {
      merge(this.template, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.template)
        )
        .subscribe((template: Template) => {
          this.logService.loadTemplateLogList(template.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = template.createdAt;
        });
    }
    if (this.receivablesBalance) {
      merge(this.receivablesBalance, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.receivablesBalance)
        )
        .subscribe((receivablesBalance: ReceivablesBalance) => {
          this.logService.loadReceivablesBalanceLogList(receivablesBalance.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = receivablesBalance?.createdAt;
          this.createdBy = receivablesBalance?.createdBy;
        });
    }
    if (this.licensedAgent) {
      merge(this.licensedAgent, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.licensedAgent)
        )
        .subscribe((licensedAgent: LicensedAgent) => {
          this.logService.loadLicensedAgentLogList(licensedAgent.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = licensedAgent.createdAt;
          this.createdBy = licensedAgent.createdBy;
        });
    }
    if (this.terminals) {
      merge(this.terminals, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.terminals)
        )
        .subscribe((terminals: Terminals) => {
          this.logService.loadTerminalsLogList(terminals.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = terminals.dateAdded;
          this.createdBy = terminals.createdBy;
        });
    }
    if (this.terminalsImportRow) {
      merge(this.terminalsImportRow, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.terminalsImportRow)
        )
        .subscribe((terminalsImportRow: TerminalsImportRow) => {
          this.logService.loadTerminalsImportRowsLogList(terminalsImportRow.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = terminalsImportRow.createdAt;
          this.createdBy = terminalsImportRow.import?.createdBy;
        });
    }
    if (this.affiliateCode) {
      merge(this.affiliateCode, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.affiliateCode)
        )
        .subscribe((affiliateCode: AffiliateCode) => {
          this.logService.loadAffiliateCodeLogList(affiliateCode.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = affiliateCode.createdAt;
          this.createdBy = affiliateCode.createdBy;
        });
    }

    if (this.personalDataChange) {
      merge(this.personalDataChange, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.personalDataChange)
        )
        .subscribe((personalDataChange: PersonalDataChange) => {
          this.logService.loadPersonalDataChangeLogList(personalDataChange.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = personalDataChange.createdAt;
          this.createdBy = personalDataChange.createdBy;
        });
    }

    if (this.receivablesTransfer) {
      merge(this.receivablesTransfer, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.receivablesTransfer)
        )
        .subscribe((receivablesTransfer: ReceivablesTransfer) => {
          this.logService.loadReceivablesTransferDataChangeLogList(receivablesTransfer.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = receivablesTransfer.createdAt;
          this.createdBy = receivablesTransfer.createdBy;
        });
    }

    if (this.employee) {
      merge(this.employee, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.employee)
        )
        .subscribe((employee: Employee) => {
          this.logService.loadEmployeeDataChangeLogList(employee.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = employee.user.createdAt;
          this.createdBy = employee.user.createdBy;
        });
    }

    if (this.leaveApplication) {
      merge(this.leaveApplication, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.leaveApplication)
        )
        .subscribe((leaveApplication: LeaveApplication) => {
          this.logService.loadLeaveAPPlicationDataChangeLogList(leaveApplication.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = leaveApplication.createdAt;
          this.createdBy = leaveApplication.createdBy;
        });
    }

    if (this.license) {
      merge(this.license, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.license)
        )
        .subscribe((license: License) => {
          this.logService.loadLicenseLogList(license.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = license.createdAt;
          this.createdBy = license.createdBy;
        });
    }

    if (this.device) {
      merge(this.device, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.device)
        )
        .subscribe((device: Device) => {
          this.logService.loadDeviceLogList(device.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = device.createdAt;
          this.createdBy = device.createdBy;
        });
    }

    if (this.warehouseProduct) {
      merge(this.warehouseProduct, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.warehouseProduct)
        )
        .subscribe((warehouseProduct: WarehouseProduct) => {
          this.logService.loadWarehouseProductLogList(warehouseProduct.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = warehouseProduct.createdAt;
          this.createdBy = warehouseProduct.createdBy;
        });
    }

    if (this.additionalField) {
      merge(this.additionalField, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.additionalField)
        )
        .subscribe((additionalField: AdditionalField) => {
          this.logService.loadAdditionalFieldLogList(additionalField.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = additionalField.createdAt;
          this.createdBy = additionalField.createdBy;
        });
    }

    if (this.competitionImportRow) {
      merge(this.competitionImportRow, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.competitionImportRow)
        )
        .subscribe((competitionImportRow: CompetitionImportRow) => {
          this.logService.loadCompetitionImportRowLogList(competitionImportRow.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = null;
          this.createdBy = null;
        });
    }


    if (this.commissionTable) {
      merge(this.commissionTable, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.commissionTable)
        )
        .subscribe((commissionTable: CommissionTable) => {
          this.logService.loadCommissionTableLogList(commissionTable.uuid, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = null;
          this.createdBy = commissionTable.createdBy;
        });
    }

    if (this.riskConfiguration) {
      merge(
        this.riskConfiguration,
        this.paginator.page
      )
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.riskConfiguration)
        )
        .subscribe(
          (riskConfiguration: RiskConfiguration) => {
            this.logService.loadRiskConfigurationLogList(
              riskConfiguration.uuid,
              this.paginator.pageIndex,
              this.paginator.pageSize
            );

            this.createdAt = null;
            this.createdBy = null;
          }
        );
    }

    if (this.hiperioRegistrationConfig) {
      merge(this.hiperioRegistrationConfig, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.hiperioRegistrationConfig)
        )
        .subscribe((hiperioRegistrationConfig: HiperioRegistrationConfig) => {
          this.logService.loadHiperioRegistrationConfigLogList(hiperioRegistrationConfig.id, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = null;
          this.createdBy = null;
        });
    }

    if (this.hiperioConfigDirect) {
      merge(this.hiperioConfigDirect, this.paginator.page)
        .pipe(
          takeUntil(this.destroyed$),
          delay(0),
          mergeMap(() => this.hiperioConfigDirect)
        )
        .subscribe((hiperioConfigDirect: HiperioConfigDirect) => {
          this.logService.loadHiperioConfigDirectLogList(hiperioConfigDirect.id, this.paginator.pageIndex, this.paginator.pageSize);
          this.createdAt = null;
          this.createdBy = null;
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onImgError($event: ErrorEvent): void {
    // @ts-ignore
    $event.target.src = '/assets/avatar.png';
  }
}
